import { Outlet } from "react-router";
import type { RouteObject } from "react-router";
import { RootRouteErrorBoundary } from "@/components/RootRouteErrorBoundary";

export interface RouteHandle {
  sidebarSection?: "projects" | "agents" | "judges" | "datasets";
  projectTab?: "overview" | "specifications" | "playground" | "results";
}
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import Workspace from "@/pages/Workspace";
import AllCodes from "@/pages/AllCodes";
import AllJudges from "@/pages/AllJudges";
import AllProjects from "@/pages/AllProjects";
import AllDatasets from "@/pages/AllDatasets";
import DatasetDetail from "@/pages/DatasetDetail";
import ErrorLogs, { ErrorLogsErrorBoundary } from "@/pages/ErrorLogs";
import NotFound from "@/pages/NotFound";
import {
  WORKSPACE_LOADER_ID,
  workspaceLayoutLoader,
} from "@/routes/workspaceLoader";
import {
  testRunListLoader,
  testRunResultsLoader,
} from "@/routes/testRunsLoader";
import { resultsLoader, RESULTS_LOADER_ID } from "@/routes/resultsLoader";
import {
  playgroundLoader,
  PLAYGROUND_LOADER_ID,
} from "@/routes/playgroundLoader";
import {
  specificationsAction,
  specificationsLoader,
  SPECIFICATIONS_LOADER_ID,
} from "@/routes/specificationsRoute";
import {
  WORKSPACE_SIDEBAR_ACTION_PATH,
  workspaceSidebarAction,
} from "@/routes/workspaceSidebarAction";
import TestDataView from "@/components/TestDataView";
import { indexLoader } from "@/routes/indexLoader";
import { allDatasetsLoader } from "@/routes/allDatasetsLoader";
import { datasetDetailLoader } from "@/routes/datasetDetailLoader";
import { errorLogsAction, errorLogsLoader } from "@/routes/errorLogsRoute";
import {
  testDataViewAction,
  testDataViewLoader,
} from "@/routes/testDataViewRoute";
import TestRunResults from "@/pages/TestRunResults";
import {
  WorkspaceCodeRoute,
  WorkspaceHomeRoute,
  WorkspaceJudgeRoute,
  WorkspaceProjectRoute,
} from "@/routes/workspaceRoutes";

const childRoutes: RouteObject[] = [
  { path: "/", element: <Index />, loader: indexLoader },
  { path: "/auth", element: <Auth /> },
  {
    id: WORKSPACE_LOADER_ID,
    loader: workspaceLayoutLoader,
    children: [
      {
        path: "/workspace",
        element: <Workspace />,
        children: [
          { index: true, element: <WorkspaceHomeRoute /> },
          {
            path: "evals/:evalId",
            element: <WorkspaceProjectRoute />,
            handle: {
              sidebarSection: "projects",
              projectTab: "overview",
            } as RouteHandle,
          },
          {
            path: "evals/:evalId/specifications",
            element: <WorkspaceProjectRoute />,
            handle: {
              sidebarSection: "projects",
              projectTab: "specifications",
            } as RouteHandle,
            id: SPECIFICATIONS_LOADER_ID,
            loader: specificationsLoader,
            action: specificationsAction,
          },
          {
            path: "evals/:evalId/playground",
            element: <WorkspaceProjectRoute />,
            handle: {
              sidebarSection: "projects",
              projectTab: "playground",
            } as RouteHandle,
            id: PLAYGROUND_LOADER_ID,
            loader: playgroundLoader,
          },
          {
            path: "evals/:evalId/results",
            element: <WorkspaceProjectRoute />,
            handle: {
              sidebarSection: "projects",
              projectTab: "results",
            } as RouteHandle,
            id: RESULTS_LOADER_ID,
            loader: resultsLoader,
          },
          {
            path: "judges/:judgeId",
            element: <WorkspaceJudgeRoute />,
            handle: { sidebarSection: "judges" } as RouteHandle,
          },
          {
            path: "codes/:codeId",
            element: <WorkspaceCodeRoute />,
            handle: { sidebarSection: "agents" } as RouteHandle,
          },
          {
            path: "test-data",
            element: <TestDataView />,
            loader: testDataViewLoader,
            action: testDataViewAction,
          },
        ],
      },
      // Agent alias route is disabled for v1.1 (canonical route remains /codes).
      // {
      //   path: "/agents",
      //   element: <AllCodes />,
      //   handle: { sidebarSection: "agents" } as RouteHandle,
      // },
      {
        path: "/codes",
        element: <AllCodes />,
        handle: { sidebarSection: "agents" } as RouteHandle,
      },
      {
        path: "/judges",
        element: <AllJudges />,
        handle: { sidebarSection: "judges" } as RouteHandle,
      },
      {
        path: "/evals",
        element: <AllProjects />,
        handle: { sidebarSection: "projects" } as RouteHandle,
      },
      {
        path: "/datasets",
        element: <AllDatasets />,
        loader: allDatasetsLoader,
        handle: { sidebarSection: "datasets" } as RouteHandle,
      },
      {
        path: "/dataset/:datasetId",
        element: <DatasetDetail />,
        loader: datasetDetailLoader,
        handle: { sidebarSection: "datasets" } as RouteHandle,
      },
      {
        path: "/error-logs",
        element: <ErrorLogs />,
        loader: errorLogsLoader,
        action: errorLogsAction,
        errorElement: <ErrorLogsErrorBoundary />,
      },
      { path: "/workspace/test-runs/list", loader: testRunListLoader },
      {
        path: "/workspace/test-runs/results",
        element: <TestRunResults />,
        loader: testRunResultsLoader,
      },
    ],
  },
  { path: WORKSPACE_SIDEBAR_ACTION_PATH, action: workspaceSidebarAction },
  { path: "*", element: <NotFound /> },
];

export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: <RootRouteErrorBoundary />,
    children: childRoutes,
  },
];
